export interface PhotonResult {
  name: string;
  address: string;
  location: { lat: number, lng: number };
  city?: string;
  postcode?: string;
  country?: string;
  type?: string;
}

export async function searchPhoton(query: string, limit: number = 40, allowedDepartments?: string[], citiesOnly?: boolean): Promise<PhotonResult[]> {
  try {
    // On demande plus de résultats (limit * 3) pour pouvoir filtrer ensuite
    // Si on filtre par département, on demande encore plus de résultats pour être sûr d'en avoir assez
    const fetchLimit = allowedDepartments && allowedDepartments.length > 0 ? limit * 5 : limit * 3;
    // Bounding box pour la France métropolitaine (minLon, minLat, maxLon, maxLat)
    const bbox = '-5.5,41.3,9.6,51.1';
    let url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=${fetchLimit}&lang=fr&bbox=${bbox}`;
    
    if (citiesOnly) {
      url += '&osm_tag=place';
    }

    let response;
    try {
      response = await fetch(url);
      if (!response.ok) throw new Error('Photon API error');
    } catch (e) {
      // Fallback to Nominatim
      let nomUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=${limit}&countrycodes=fr`;
      if (citiesOnly) {
        nomUrl += '&featuretype=settlement';
      }
      const nomResponse = await fetch(nomUrl, {
        headers: {
          'Accept-Language': 'fr'
        }
      });
      if (!nomResponse.ok) throw new Error('Nominatim API error');
      const nomData = await nomResponse.json();
      return nomData.map((item: any) => ({
        name: item.display_name.split(',')[0],
        address: item.display_name,
        location: { lat: parseFloat(item.lat), lng: parseFloat(item.lon) },
        city: item.address?.city || item.address?.town || item.address?.village,
        postcode: item.address?.postcode,
        country: item.address?.country || 'France',
        type: item.type
      }));
    }
    
    const data = await response.json();
    
    return data.features
      .filter((feature: any) => {
        const p = feature.properties;
        // Filtrage strict sur la France
        const isFrance = p.country === 'France' || p.countrycode === 'FR' || p.countrycode === 'fr';
        if (!isFrance) return false;

        // Filtrage par département si demandé
        if (allowedDepartments && allowedDepartments.length > 0) {
          // On vérifie si le code postal commence par l'un des départements autorisés
          // Si le code postal n'est pas fourni par Photon, on exclut par sécurité (ou on pourrait vérifier le county/state)
          if (!p.postcode) return false;
          
          const dept = p.postcode.substring(0, 2);
          return allowedDepartments.includes(dept);
        }

        if (citiesOnly) {
          const cityTypes = ['city', 'town', 'village', 'hamlet', 'locality'];
          if (!cityTypes.includes(p.type)) return false;
        }

        return true;
      })
      .map((feature: any) => {
        const p = feature.properties;
        const coords = feature.geometry.coordinates;
        
        // Build a readable address
        let addressParts = [];
        if (citiesOnly) {
          if (p.city || p.name) addressParts.push(p.city || p.name);
          if (p.postcode) addressParts.push(p.postcode);
        } else {
          if (p.street) addressParts.push(p.street);
          if (p.housenumber) addressParts.push(p.housenumber);
          if (p.postcode) addressParts.push(p.postcode);
          if (p.city) addressParts.push(p.city);
          if (p.country) addressParts.push(p.country);
        }
        
        return {
          name: p.name || p.street || p.city || 'Inconnu',
          address: addressParts.join(', '),
          location: { lat: coords[1], lng: coords[0] },
          city: p.city,
          postcode: p.postcode,
          country: p.country,
          type: p.type
        };
      })
      .slice(0, limit); // On ne garde que le nombre de résultats demandé (5 par défaut)
  } catch (error) {
    return [];
  }
}

export async function geocodePhoton(name: string, address: string): Promise<PhotonResult | null> {
  const query = `${name} ${address}`.trim();
  const results = await searchPhoton(query, 1);
  return results.length > 0 ? results[0] : null;
}
